import {
  Column,
  GridApi,
  GridOptions,
  ICellEditorParams,
  RowNode,
} from '@ag-grid-community/core'
import { ElementRef } from '@angular/core'
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
import {
  DisplayDateFormat,
  ISODateFormat,
  isValidDate,
} from '@northfork/basics'
import { ErrorMessage } from '@northfork/ng-basics'
import { differenceInCalendarDays, min } from 'date-fns'
import { environment } from 'environments/environment'
import { CounterParty } from '../model/counter-party.model'
import { BUY_TYPES, DataMapType, DRAW_TYPES } from '../model/draw-model'
import { InventoryType } from '../model/inventory-type.model'
import { MvtBasesInOut } from '../model/mvt-basis-in-out.model'
import { Security } from '../model/security.model'
import { ExternalMvt } from '../model/single-transaction/market-value-transfer.model'
import { TransactionModalInput } from '../model/single-transaction/popup-input.model'
import {
  SecurityType,
  securityTypeVal,
} from '../model/single-transaction/security-transfer/security-type.model'
import { TransactionMatch } from '../model/single-transaction/transaction-match'
import { TransactionFormData } from '../model/single-transaction/validation-view.model'
import {
  TranCategory,
  TranCategoryBulk,
} from '../model/transaction-category.model'
import {
  CASH_ENTRIES,
  DIRECT_TRANSFER,
  ENTRIES,
  EXITS,
  FX_ENTRIES,
  SECURITY_TRANSFER_IN,
  SECURITY_TRANSFER_OUT,
  TRADE_ENTRIES,
  TransactionType,
  TransactionTypeEnum,
  TransactionTypeKey,
  TransactionTypes,
  transactionTypesVal,
  TRANSFERS,
} from '../model/transaction-type.model'
import { Basis } from './../model/mvt-basis.model'
import { TransactionConstants } from './constants'
import {
  isEditModal,
  isExternalTransaction,
  isViewOnlyModal,
} from './popup-input-util'

/**
 * Function to fetch category specific to Cash Entry
 */
export function getCashEntryType(): TransactionType[] {
  return transactionTypesVal.filter(data => CASH_ENTRIES.includes(data.name))
}

/**
 * Function to fetch category specific to Trade Entry
 */
export function getTradeTypes(): TransactionType[] {
  return transactionTypesVal.filter(data => TRADE_ENTRIES.includes(data.name))
}

/**
 *
 */
export function getTransferTypes(): TransactionType[] {
  return transactionTypesVal.filter(data => TRANSFERS.includes(data.name))
}

/**
 * Function to fetch datamap options for draws
 */
export function getDrawTypes(): DataMapType[] {
  return DRAW_TYPES
}

/**
 * Function to fetch datamap options for buys
 */
export function getBuyTypes(): DataMapType[] {
  return BUY_TYPES
}

/**
 * Function to establish if transTypeCode passed is a trade type
 */
export function isTradeType(transTypeCode: TransactionTypeKey): boolean {
  return TRADE_ENTRIES.includes(TransactionTypeEnum[transTypeCode])
}

/**
 * Function to establish if transTypeCode passed is a Fx type
 */
export function isFXType(transTypeCode: TransactionTypeKey): boolean {
  return FX_ENTRIES.includes(TransactionTypeEnum[transTypeCode])
}

/**
 * Function to establish if transTypeCode passed is a trade type
 */
export function isCashEntry(transTypeCode: TransactionTypeKey): boolean {
  return CASH_ENTRIES.includes(TransactionTypeEnum[transTypeCode])
}

/**
 * Function to establish if transTypeCode passed is a trade type
 */
export function isDirectTransfer(transTypeCode: TransactionTypeKey): boolean {
  return DIRECT_TRANSFER.includes(TransactionTypeEnum[transTypeCode])
}

/**
 * Function to establish if transTypeCode passed is a transfer type
 */
export function isTransferType(transTypeCode: TransactionTypeKey): boolean {
  return TRANSFERS.includes(TransactionTypeEnum[transTypeCode])
}

/**
 * Function to establish if transTypeCode passed is a transfer In type
 */
export function isTransferInType(transTypeCode: TransactionTypeKey): boolean {
  return SECURITY_TRANSFER_IN.includes(TransactionTypeEnum[transTypeCode])
}

/**
 * Function to establish if transTypeCode passed is a transfer Out type
 */
export function isTransferOutType(transTypeCode: TransactionTypeKey): boolean {
  return SECURITY_TRANSFER_OUT.includes(TransactionTypeEnum[transTypeCode])
}

/**
 * Function to establish if transTypeCode passed is a isLotEntry Type
 */
export function isLotEntryType(transTypeCode: TransactionTypeKey): boolean {
  return ENTRIES.includes(TransactionTypeEnum[transTypeCode])
}

/**
 * Function to establish if transTypeCode passed is a isLotExit Type
 */
export function isLotExitType(transTypeCode: TransactionTypeKey): boolean {
  return EXITS.includes(TransactionTypeEnum[transTypeCode])
}

/**
 * get counter party name from id
 *
 * @param counterPartId counterPartId
 * @param counterPartyList counterPartyList
 */
export function getCounterPartyFromId(
  counterPartId: number,
  counterPartyList: CounterParty[],
): CounterParty {
  const validCounterParties = counterPartyList.filter(
    counterParty => counterParty.id == counterPartId,
  )
  return validCounterParties.length > 0
    ? validCounterParties[0]
    : counterPartyList.filter(
        cp => cp.id == TransactionConstants.DEFAULT_COUNTER_PARTY_ID,
      )[0]
}

/**
 * get counter party from name
 *
 * @param counterPartyName counterPartName
 * @param counterPartyList counterPartyList
 */
export function getCounterPartyFromName(
  counterPartyName: string,
  counterPartyList: CounterParty[],
): CounterParty {
  const validCounterParties = counterPartyList.filter(
    counterParty => counterParty.name == counterPartyName,
  )
  return validCounterParties.length > 0
    ? validCounterParties[0]
    : counterPartyList.filter(
        cp => cp.id == TransactionConstants.DEFAULT_COUNTER_PARTY_ID,
      )[0]
}

/**
 * Get transaction code from name
 */
export function getTransactionTypeCodeFromName(
  transType: TransactionTypeEnum,
): string {
  return getTransactionTypeFromName(transType)?.code
}

/**
 * Get transaction type from name
 */
export function getTransactionTypeFromName(
  transType: TransactionTypeEnum,
): TransactionType {
  return TransactionTypes[transType]
}

/**
 * Get transaction type from code
 */
export function getTransactionTypeKeyFromCode(
  transTypeCode: string,
): TransactionTypeKey {
  const transType = transactionTypesVal.find(val => val.code == transTypeCode)
  if (!transType) {
    return TransactionTypeEnum.UNSUPPORTED
  }
  return transType.transactionTypeKey
}
/**
 *  mapping key to code
 */
export function getTransactionTypeCodeFromKey(
  transTypeKey: TransactionTypeKey,
): string {
  const transType = transactionTypesVal.find(
    val => val.transactionTypeKey == transTypeKey,
  )
  if (!transType) {
    return TransactionTypeEnum.UNSUPPORTED
  }
  return transType.code
}
/**
 * mapping Transaction Type from  Transaction code
 */
export function getTransactionTypeNameFromCode(
  transTypeCode: string,
): TransactionTypeEnum {
  const transType = transactionTypesVal.find(val => val.code == transTypeCode)
  if (!transType) {
    return TransactionTypeEnum.UNSUPPORTED
  }
  return transType.name
}

/**
 * Get transaction type from name
 */
export function getTransactionTypeKeyFromName(
  name: TransactionTypeEnum,
): TransactionTypeKey {
  return getTransactionTypeFromName(name).transactionTypeKey
}

/**
 * Get transaction category from code
 */
export function getTransactionCategoryFromCode(
  transTypeCode: string,
): TranCategory {
  const transType: TransactionTypeKey =
    getTransactionTypeKeyFromCode(transTypeCode)
  return getTransactionCategoryFromTypeKey(transType)
}

/**
 * Get transaction category from transaction type key
 */
export function getTransactionCategoryFromTypeKey(
  transType: TransactionTypeKey,
): TranCategory {
  if (isTradeType(transType)) {
    return TranCategory.TRADE
  }
  if (isTransferType(transType)) {
    return TranCategory.TRANSFER
  }
  if (isCashEntry(transType)) {
    return TranCategory.CASH_ENTRY
  }
  if (isFXType(transType)) {
    return TranCategory.FX_TRADE
  }
  if (transType == 'CURRENCY_FORWARD') {
    return TranCategory.CURRENCY_FORWARD
  }
  if (isDirectTransfer(transType)) {
    return TranCategory.DIRECT_TRANSFER
  }
  return TranCategory.OTHER
}

/**
 * Get transaction category from transaction type key for bulk modals
 */
export function getTransactionCategoryFromTypeKeyForBulk(
  transType: TransactionTypeKey,
): TranCategoryBulk {
  if (isTradeType(transType)) {
    return TranCategoryBulk.TRADE
  }
  if (isTransferInType(transType)) {
    return TranCategoryBulk.TRANSFER_IN
  }
  if (isTransferOutType(transType)) {
    return TranCategoryBulk.TRANSFER_OUT
  }
  if (transType == 'INITIAL_LOAD') {
    return TranCategoryBulk.LOAD
  }
  if (isCashEntry(transType)) {
    return TranCategoryBulk.CASH_ENTRY
  }
  if (isFXType(transType)) {
    return TranCategoryBulk.FX_TRADE
  }
  if (transType == 'CURRENCY_FORWARD') {
    return TranCategoryBulk.CURRENCY_FORWARD
  }
  if (isDirectTransfer(transType)) {
    return TranCategoryBulk.DIRECT_TRANSFER
  }
  return TranCategoryBulk.OTHER
}

/**
 * Remove invalidation i.e warning or error from a transaction row
 */
export function removeInvalidation(rowNode: RowNode) {
  if (!rowNode) return

  const data = rowNode.data ?? {}
  data.validityStatus = 'valid'
  data.errorMessage = ''
  rowNode.setData(data)
}

/**
 * Add error invalidation  to a transaction row
 */
export function addErrorDetails(rowNode: RowNode, errorMessage: string) {
  if (!rowNode) return

  const data = rowNode.data ?? {}
  data.validityStatus = 'invalid'
  data.errorMessage = errorMessage
  rowNode.setData(data)
}

/**
 * Add warning invalidation  to a transaction row
 */
export function addWarningDetails(rowNode: RowNode, errorMessage: string) {
  if (!rowNode) return

  const data = rowNode.data ?? {}
  data.validityStatus = 'warning'
  data.errorMessage = errorMessage
  rowNode.setData(data)
}

/**
 * handle rowNode Validation
 */
export function handleValidation(
  rowNode: RowNode,
  validationState: string,
  validationMessage: string,
) {
  switch (validationState) {
    case 'VALID':
      removeInvalidation(rowNode)
      break
    case 'WARNING':
      addWarningDetails(rowNode, validationMessage)
      break
    case 'INVALID':
      addErrorDetails(rowNode, validationMessage)
      break
  }
}

/** Hover text on the account row while it is unresolved (red). */
export const ACCOUNT_REQUIRED_ERROR =
  'Select an account to validate this transaction'
/** Hover text on a field edited before an account is selected (yellow). */
export const ACCOUNT_REQUIRED_FIELD_WARNING =
  'Select an account before this field can be validated'

/**
 * Clears warning/error marks from every row. Used when an account becomes resolved, just
 * before re-running validation, so the account-unresolved marks don't linger.
 */
export function clearAllInvalidations(api: GridApi | undefined) {
  api?.forEachNode(node => {
    if (node.data) removeInvalidation(node)
  })
  api?.refreshCells({ force: true })
}

/**
 * Re-runs account-scoped field lookups that were deferred while the account was unresolved.
 * Once a concrete account is picked, call this with the field-driven lookups so already-entered
 * values (e.g. an Asset ID typed before the account) pull their backend-derived columns. Each
 * lookup runs only when its source cell currently holds a value.
 */
export function replayDeferredLookups(
  api: GridApi | undefined,
  lookups: { rowId: string; run: (value: string) => void }[],
): void {
  if (!api) return
  lookups.forEach(({ rowId, run }) => {
    const value = api.getRowNode(rowId)?.data?.value
    if (value !== undefined && value !== null && value !== '') run(value)
  })
}

/**
 * calculateBasisPrice
 */
export function calculateBasisPrice(
  cost: number,
  factorizedUnits: number,
  priceScalar: number,
  contractSize: number,
): number | undefined {
  const price = cost / factorizedUnits / priceScalar / contractSize // Forcing division order prevents rounding issues

  if (Number.isFinite(price)) {
    return price
  }

  return undefined
}

export const CURRENCY_ONLY_TRANSACTION_TYPES: TransactionTypeEnum[] = [
  TransactionTypeEnum.CASH_TRANSFER,
  TransactionTypeEnum.MANAGEMENT_FEE,
  TransactionTypeEnum.CUSTODY_FEE,
  TransactionTypeEnum.OVERDRAFT_CHARGE,
]
export const TransactionType_FOR_SHORT: TransactionTypeEnum[] = [
  TransactionTypeEnum.BUY_COVER,
  TransactionTypeEnum.SHORT_SELL,
]

/**
 * calculate proceeds for trade entry and others
 */
export function calculateProceeds(
  price: number,
  accruedInterest: number,
  units: number,
  factor: number,
  commission: number,
  originationFee: number,
  drawFee: number,
  exitFee: number,
  exitFeeReceivable: number,
  prepaymentPenaltyFee: number,
  prepaidInterest: number,
  interestDue: number,
  reserveTotal: number,
  priceScalar: number,
  contractSize: number,
  negate: boolean,
  hplMode: boolean,
) {
  const multiplier = negate ? -1 : 1
  let proceeds: number = calculateProceedsNonRounded(
    price,
    accruedInterest,
    units,
    factor,
    commission,
    originationFee,
    drawFee,
    exitFee,
    exitFeeReceivable,
    prepaymentPenaltyFee,
    prepaidInterest,
    interestDue,
    reserveTotal,
    priceScalar,
    contractSize,
    multiplier,
  )

  const sign: number = proceeds < 0 ? -1 : 1
  const roundingValue = hplMode ? 10000 : 100
  proceeds =
    (Math.round(Math.abs(proceeds) * roundingValue) / roundingValue) * sign // round to two decimal places

  return proceeds
}

/**
 * calculate proceeds for trade entry and others
 */
export function calculateProceedsNonRounded(
  price: number,
  accruedInterest: number,
  units: number,
  factor: number,
  commission: number,
  originationFee: number,
  drawFee: number,
  exitFee: number,
  exitFeeReceivable: number,
  prepaymentPenaltyFee: number,
  prepaidInterest: number,
  interestDue: number,
  reserveTotal: number,
  priceScalar: number,
  contractSize: number,
  multiplier: -1 | 1,
) {
  return (
    (price + accruedInterest) *
      (units * factor * contractSize * priceScalar * multiplier) -
    commission +
    originationFee +
    drawFee +
    exitFee +
    exitFeeReceivable +
    prepaymentPenaltyFee +
    prepaidInterest -
    interestDue +
    reserveTotal
  )
}

/**
 * calculate discount for trade entry and others
 */
export function calculateDiscount(
  price: number,
  settleDate: Date,
  security?: Security,
): number {
  if (!security) {
    return 0.0
  }
  if (
    !(
      isTBill(security) ||
      security.securityTypeData?.isCp ||
      isAgencyDiscount(security) ||
      security.securityTypeData?.isZeroCoupon
    )
  ) {
    return 0.0
  }
  const redemptionDate = getRedemptionDate(security)
  if (redemptionDate == null) return 0.0
  const dateDiff = differenceInCalendarDays(redemptionDate, settleDate)
  const scalar = security.securityMasterData.priceScalar
  if (scalar == 0.0 || dateDiff <= 0.0) return 0.0
  return ((1 - price * scalar) * 360) / scalar / dateDiff
}

/**
 * get is AgencyDiscount
 *
 * @param security security
 * @returns isTbill
 */
function isAgencyDiscount(security: Security) {
  return (
    security.issuerTypeData?.isGovernmentAgency &&
    security.securityTypeData?.isDiscount
  )
}

/**
 * get redemption Date
 *
 * @param security security
 * @returns isTbill
 */
function getRedemptionDate(security: Security): Date | null {
  const dateArray: Date[] = []
  if (
    security.securityMasterData.maturityDate &&
    security.securityMasterData.maturityDate != null
  ) {
    dateArray.push(new Date(security.securityMasterData.maturityDate))
  }
  if (
    security.securityMasterData.preRefundDate &&
    security.securityMasterData.preRefundDate != null
  ) {
    dateArray.push(new Date(security.securityMasterData.preRefundDate))
  }
  if (
    security.securityMasterData.mandatoryPutDate &&
    security.securityMasterData.mandatoryPutDate != null
  ) {
    dateArray.push(new Date(security.securityMasterData.mandatoryPutDate))
  }
  return dateArray.length > 0 ? min(dateArray) : null
}

/**
 * get is tbill
 *
 * @param security security
 * @returns isTbill
 */
function isTBill(security: Security) {
  return (
    security.securityTypeData?.isTreasuryBill &&
    security.securityMasterData.countryDomicile == 'US'
  )
}

/**
 * get unknown counterparty
 */
export function getDefaultUnknownCounterParty(): CounterParty {
  return {
    id: 13,
    shortName: '---',
    name: 'Unknown',
    active: true,
    dtc: 0,
    institutionId: 0,
    agentId: 0,
  }
}

/**
 * createMetadata
 */
export function createMetadata(
  reconDate: Date,
  cusip: string,
  unclearTransaction?: boolean,
  reprocessDownstreamTransfers?: boolean,
): Record<string, unknown> {
  return {
    reconDate,
    cusip,
    unclearTransaction,
    reprocessDownstreamTransfers,
  }
}

/**
 * createMetadata for transfer
 */
export function createMetadataSecurityTransfer(
  reconDate: Date,
  cusip: string,
  mvtBases: { [k: string]: Basis },
  externalMvtBases: { [k: string]: ExternalMvt },
  unclearTransaction: boolean,
  reprocessDownstreamTransfers: boolean,
): Record<string, unknown> {
  return {
    ...createMetadata(
      reconDate,
      cusip,
      unclearTransaction,
      reprocessDownstreamTransfers,
    ),
    mvtBases,
    externalMvtBases,
  }
}

/**
 * createMetadata for direct transfer
 */
export function createMetadataDirectTransfer(
  reconDate: Date,
  cusip: string,
  destAccountId: number,
  unitsRatio: number,
  costBasisProportion: number,
  destSecurityId: number,
  inBases: Basis[],
  exchangeType: string,
  unclearTransaction: boolean,
  mvtBases?: MvtBasesInOut,
): Record<string, unknown> {
  return {
    ...createMetadata(reconDate, cusip, unclearTransaction),
    destAccountId,
    unitsRatio,
    costBasisProportion,
    destSecurityId,
    inBases,
    exchangeType,
    mvtBases,
  }
}
/**
 * check if env is prod
 */
export function isProdEnv() {
  return environment.production
}

/**
 * Get current date in YYYY-MM-DD format
 *
 * @returns date in iso string
 */
export function getStringISOForDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

/**
 * check if security is non commision
 */
export function isSecurityNonCommission(sec: Security): boolean {
  const secType = sec.securityType
  return sec.securityTypeData
    ? !(
        sec.securityTypeData.isAuctionRate &&
        sec.securityTypeData.isPreferredStock
      ) &&
        !sec.securityTypeData.isEquity &&
        !(
          sec.securityTypeData.isMutualFund &&
          !sec.securityTypeData.isMoneyMarketFund
        ) &&
        !sec.securityTypeData.isPreferredStock &&
        !sec.securityTypeData.isInterestOnly &&
        !sec.securityTypeData.isOption &&
        !sec.securityTypeData.isFuture &&
        !sec.securityTypeData.isBabyBond &&
        !(
          secType == securityTypeVal.get(SecurityType.OTHER_ASSET)?.abbrev &&
          sec.securityTypeData.isWarrant
        ) &&
        secType != securityTypeVal.get(SecurityType.SPOT_CURRENCY)?.abbrev &&
        secType != securityTypeVal.get(SecurityType.FORWARD_CURRENCY)?.abbrev &&
        secType != securityTypeVal.get(SecurityType.FUTURE)?.abbrev &&
        secType != securityTypeVal.get(SecurityType.OTHER)?.abbrev
    : false
}

/**
 * get error/warning formatter fiedl wise
 *
 * field field to create formatter for
 *
 * @returns ErrorMessages
 */
export function getFieldValidatorFormatter(field: string): ErrorMessage[] {
  return [
    {
      error: field + 'Validator',
      format: (label, error): string => {
        return error.message
      },
    },
  ]
}

/**
 * get cell class on validityStatus
 *
 * @returns cell class
 */
export function deduceValidityStatusCellClass(validityStatus: string) {
  return validityStatus === 'inactive'
    ? 'mdi mdi-close-circle-outline text-muted'
    : validityStatus === 'valid'
    ? 'mdi mdi-check-circle text-success'
    : validityStatus === 'invalid'
    ? 'mdi mdi-close-circle-outline text-danger'
    : validityStatus === 'intermediate'
    ? 'mdi mdi-file-edit text-info'
    : validityStatus === 'warning'
    ? 'mdi mdi-alert-circle-outline text-warning'
    : ''
}
/**
 * convert toISOFormatFromAnyValidDateString
 */
export function toISOFormatFromAnyValidDateString(
  inputDateString: string,
): string {
  //pasting iso dates skip
  const isoRegex = new RegExp('^\\d{4}-\\d{2}-\\d{2}$')
  if (isoRegex.test(inputDateString)) {
    return inputDateString
  }

  const inputDate = new Date(inputDateString)
  let month = '' + (inputDate.getMonth() + 1)
  let day = '' + inputDate.getDate()
  const year = inputDate.getFullYear()

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  return [year, month, day].join('-')
}

/**
 * prepare multiview for multimatch validation
 */
export function prepareMultiViewForMultiMatchValidations(
  transactionMatches: TransactionMatch[],
): TransactionFormData[] {
  return transactionMatches.map(tranMatch => {
    return { transaction: tranMatch }
  })
}

/**
 * fill handle double click handler
 */
/**
 * Points the user at a field-row's editable cell and opens its editor. Used when a sleeve
 * aggregate is loaded and a sub-account still needs to be chosen, so the account picker is
 * the obvious next step.
 */
export function focusFieldRowEditor(api: GridApi, rowId: string) {
  // Defer to the next tick so the row is editable after the current refresh cycle.
  setTimeout(() => {
    let found: number | null = null
    api.forEachNode((node: RowNode) => {
      if (found == null && node.data?.id === rowId && node.rowIndex != null) {
        found = node.rowIndex
      }
    })
    const rowIndex = found
    if (rowIndex == null) return
    api.ensureIndexVisible(rowIndex)
    api.startEditingCell({ rowIndex, colKey: 'value' })
  })
}

export function fillHandleDoubleClickHandlerSingleDialog(api: GridApi) {
  // get selection range
  const ranges = api.getCellRanges()
  if (!ranges || ranges.length == 0) {
    return
  }
  const range = ranges[0]
  // if more than 1 row leave
  if (range.startRow?.rowIndex != range.endRow?.rowIndex) {
    return
  }
  const startingColumn = range.columns[0]
  // copy values in all  rows below starting row in same column as long as type is same
  const startRowIndex = range.startRow ? range.startRow?.rowIndex : 0
  const startingCellFieldData = api.getDisplayedRowAtIndex(startRowIndex)?.data
  const startingColumnValue = startingCellFieldData[startingColumn.getColId()]
  const startingFieldType = getCellDataTypeForSingleDialogs(
    startingColumn,
    startingCellFieldData,
  )
  const targetNodes = api
    .getRenderedNodes()
    .filter(rowNode => Number(rowNode.rowIndex) > startRowIndex)
  for (const rowNodeEffected of targetNodes) {
    const cellType =
      getCellDataTypeForSingleDialogs(startingColumn, rowNodeEffected.data) ??
      'String'
    const options = getCellOptionsForSingleDialogs(
      startingColumn,
      rowNodeEffected.data,
    )
    const items = options
      ? options.bindValue
        ? options.items.map(
            (item: { [x: string]: unknown }) => item[options.bindValue],
          )
        : options.items
      : undefined
    if (
      startingColumn.isCellEditable(rowNodeEffected) &&
      startingFieldType == cellType &&
      allowUpdateBasedOnType(startingColumnValue, cellType, items)
    ) {
      api
        .getRowNode(rowNodeEffected.id ?? '')
        ?.setDataValue(startingColumn, startingColumnValue)
    } else {
      break
    }
  }
}

/**
 * find cell data type
 *
 * @param column column to check data type for
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getCellDataTypeForSingleDialogs(column: Column, rowData: any) {
  const cellEditorSelector = column.getColDef().cellEditorSelector
  const cellEditorSelectorResult = cellEditorSelector
    ? cellEditorSelector({ data: rowData } as ICellEditorParams)
    : undefined
  return (
    column.getColDef().cellEditorParams({ data: rowData }).dataType ??
    cellEditorSelectorResult?.params.dataType
  )
}

/**
 * emptySelectedCellsOnValidKeyPress an open ended function to accumulate more delete like keys like backspace or escape
 */
export function emptySelectedCellsOnValidKeyPress(
  keyPressed: string,
  api: GridApi,
) {
  if (TransactionConstants.DELETE_BULK_KEYS.includes(keyPressed)) {
    emptySelectedCellsInRanges(api)
  }
}

/**
 * emptySelectedCellsInRange
 */
export function emptySelectedCellsInRanges(api: GridApi) {
  // get selection range
  const ranges = api.getCellRanges()
  if (!ranges || ranges.length == 0) {
    return
  }
  ranges.forEach(range => {
    // copy values in all active rows that is rows with data below the range of columns and 1 row
    let startRowIndex = range.startRow?.rowIndex ?? 0
    let endRowIndex = range.endRow?.rowIndex ?? 0
    // Swap indices if selection was made in reverse order (bottom-to-top)
    if (startRowIndex > endRowIndex)
      [startRowIndex, endRowIndex] = [endRowIndex, startRowIndex]
    api
      .getRenderedNodes()
      .filter(
        rowNode =>
          Number(rowNode.rowIndex) >= startRowIndex &&
          Number(rowNode.rowIndex) <= endRowIndex,
      )
      .forEach(rowNodeEffected => {
        range.columns
          .filter(column => column.isCellEditable(rowNodeEffected))
          .forEach(column => {
            api.getRowNode(rowNodeEffected.id ?? '')?.setDataValue(column, '')
          })
      })
  })
}

/**
 * Flags every active row whose `detectionField` is empty with an "Account is required"
 * error on `impactedField`, so the bulk Validate gate can stop before any backend call.
 * Returns true if any row was flagged; the caller owns updating its own `isDataValid`.
 */
export function markRowsMissingAccount(
  api: GridApi | undefined,
  detectionField: string,
  impactedField: string,
  cellLevelErrorMessageMap: Map<string, string[]>,
): boolean {
  let anyMissing = false
  api?.forEachNode(node => {
    const data = node.data
    if (!data || data.validityStatus === 'inactive' || data[detectionField]) {
      return
    }
    anyMissing = true
    const messages = ['Account is required']
    const errorMessageMap = new Map<string, string[]>(
      data.errorMessageMap ?? [],
    )
    errorMessageMap.set(impactedField, messages)
    data.errorMessageMap = errorMessageMap
    data.validityStatus = 'invalid'
    cellLevelErrorMessageMap.set(`${data.rowId}-${impactedField}`, messages)
  })
  if (anyMissing) {
    api?.refreshCells({ force: true })
  }
  return anyMissing
}

/**
 * allowUpdateBasedOnType
 */
export function allowUpdateBasedOnType(
  data: string,
  type: 'Date' | 'Number' | 'String' | 'Select',
  items?: string[],
) {
  switch (type) {
    case 'Number':
      return !Number.isNaN(Number(data))
    case 'Date':
      return (
        isValidDate(data, ISODateFormat) || isValidDate(data, DisplayDateFormat)
      )
    case 'Select':
      return items?.includes(data)
    case 'String':
      return true
  }
}

/**
 * isSingleValueCase in paste
 */
export function isSingleValueCase(rowWiseData: string[]) {
  if (rowWiseData.length != 1) {
    return false
  }
  const allCellData = rowWiseData[0].split('\t')
  if (allCellData.length != 1) {
    return false
  }
  return true
}

/**
 * getCellOptionsForSingleDialogs
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getCellOptionsForSingleDialogs(column: Column, rowData: any) {
  const cellEditorSelector = column.getColDef().cellEditorSelector
  const cellEditorSelectorResult = cellEditorSelector
    ? cellEditorSelector({ data: rowData } as ICellEditorParams)
    : undefined
  return (
    column.getColDef().cellEditorParams({ data: rowData }).options ??
    cellEditorSelectorResult?.params.options
  )
}

/**
 * activateDialog activateDialog
 */
export function activateDialog(
  activeInstances: NgbModalRef[],
  compId: string,
  elRef: ElementRef,
) {
  const isStickyDialogueActive = activeInstances
    .filter(inst => inst.componentInstance.getComponentId)
    .find(instance =>
      TransactionConstants.STICKY_DIALOGUES.includes(
        instance.componentInstance.getComponentId(),
      ),
    )
  if (!isStickyDialogueActive) {
    setHighestZIndexForOpenModals(compId, elRef)
    unFocusGridsForInactivatedModals(activeInstances, compId)
  }
}

/**
 * check if compId passed is a InActive modal
 */
export function isDialogComponentInActive(compId: string) {
  const baseZIndex = sessionStorage.getItem('highestZIndex')
  return baseZIndex == null || JSON.parse(baseZIndex).componentId != compId
}

/**
 * Gets the ultimate NGB-MODAL-WINDOW parent element for a given element
 */
export function getParentModalWindow(elRef: ElementRef) {
  let parentModalWindow = elRef.nativeElement.parentNode
  while (
    parentModalWindow &&
    parentModalWindow.tagName !== 'NGB-MODAL-WINDOW'
  ) {
    parentModalWindow = parentModalWindow.parentNode
  }
  return parentModalWindow
}

/**
 * registerToActiveGrids for interModal sanity
 */
export function setHighestZIndexForOpenModals(
  currentCompId: string,
  elRef: ElementRef,
) {
  const parentModalWindow = getParentModalWindow(elRef)
  if (!parentModalWindow) {
    return
  }

  const baseZIndex = sessionStorage.getItem('highestZIndex')
  const newHighestZIndex =
    (baseZIndex != null ? JSON.parse(baseZIndex).zIndex : 1000) + 1
  sessionStorage.setItem(
    'highestZIndex',
    JSON.stringify({ componentId: currentCompId, zIndex: newHighestZIndex }),
  )

  const zIndexStyle = 'z-index: ' + newHighestZIndex + '!important'
  parentModalWindow.setAttribute('style', zIndexStyle)
}
/**
 * unFocusGridsForInactivatedModals
 */
export function unFocusGridsForInactivatedModals(
  activeInstances: NgbModalRef[],
  currentCompId: string,
) {
  activeInstances
    .filter(
      modalCurrent =>
        modalCurrent.componentInstance.getComponentId &&
        currentCompId != modalCurrent.componentInstance.getComponentId(),
    )
    .forEach(activeModalInstance =>
      activeModalInstance.componentInstance.disableGridFocus(),
    )
}
/**
 *Function to return default inventoryType based on Transaction Type
 */
export function getValidInventory(transType: TransactionTypeKey): string {
  if (TransactionType_FOR_SHORT.includes(TransactionTypeEnum[transType])) {
    return InventoryType.SHORT
  } else if (
    TransactionTypeEnum[transType] === TransactionTypeEnum.OPTION_WRITE
  ) {
    return InventoryType.ISSUE
  } else {
    return InventoryType.LONG
  }
}

/**
 *  Log bulk dialog metrics
 */
export function singleDialogMetricsDetail(
  transInput: TransactionModalInput | undefined,
) {
  let type = 'Create'
  if (isViewOnlyModal(transInput)) {
    type = 'View'
  } else if (isEditModal(transInput)) {
    type = 'Edit'
  } else if (isExternalTransaction(transInput)) {
    type = 'External'
  }
  return type
}

/**
 * Formats a number with thousand separators.
 */
export function formatNumberWithThousandSeparator(type: string, value: string) {
  if (type === 'number' && value) {
    return parseFloat(value).toLocaleString('en-US', {
      maximumFractionDigits: 20,
    })
  }
  return value
}

export const SINGLE_DIALOG_EVENT_NAME = 'heliosSingleDialogLoadTime'
export const SINGLE_DIALOG_VALIDATION_EVENT_NAME =
  'heliosSingleDialogValidationTime'

/**
 * Row ids that render the internal "Asset ID" (CUSIP) field in the
 * single-transaction Edit/View dialogs.
 */
export const INTERNAL_ASSET_ID_ROW_IDS = ['cusip', 'oldCusip', 'newCusip']

export function isInternalAssetIdRow(rowId: string | undefined): boolean {
  return rowId != null && INTERNAL_ASSET_ID_ROW_IDS.includes(rowId)
}

/**
 * PVAL-25738: ag-grid external-filter options that hide the internal Asset ID
 * rows (cusip/oldCusip/newCusip) from view whenever `hideRows()` returns true.
 *
 * The row stays in the grid model — only hidden from display — so it remains
 * addressable via `getRowNode(...)`. When `getRowNodeMap` is supplied, every
 * row (including the hidden ones) is registered in that map so the dialogs'
 * `rowNodeMap.get('cusip')` lookups keep resolving (value `''`) for save,
 * validation, and pricing exactly as they did before the row was hidden.
 *
 * Call `gridApi.onFilterChanged()` once the user type is known so the filter
 * re-applies.
 */
export function internalAssetIdFilterGridOptions(
  hideRows: () => boolean,
  getRowNodeMap?: () => Map<string, RowNode>,
): Pick<GridOptions, 'isExternalFilterPresent' | 'doesExternalFilterPass'> {
  return {
    isExternalFilterPresent: () => hideRows(),
    doesExternalFilterPass: node => {
      const id = node.data?.id
      if (id) getRowNodeMap?.().set(id, node)
      return !isInternalAssetIdRow(id)
    },
  }
}
